import React, { useState } from 'react';
import { Timeline, Typography, Card, Avatar, Collapse, List, Modal, Image, Button, Spin } from 'antd';
import { FileTextOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, RightOutlined, DownOutlined, FileOutlined, FileImageOutlined, FilePdfOutlined, VideoCameraOutlined, DownloadOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { TicketHistory as TicketHistoryType, TicketFile } from '../types/ticket';
import { apiService } from '../services/api';
import moment from 'moment';
import FileAttachment from './FileAttachment';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface TicketHistoryProps {
  histories: TicketHistoryType[];
  token?: string | null;
}

const TicketHistory: React.FC<TicketHistoryProps> = ({ histories, token }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Sort histories by date (newest first)
  const sortedHistories = [...histories].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const getActionIcon = (action: string) => {
    const actionMap: { [key: string]: React.ReactNode } = {
      'created': <CheckCircleOutlined className="history-dot-icon" />,
      'approved': <CheckCircleOutlined className="history-dot-icon" />,
      'process_accepted': <CheckCircleOutlined className="history-dot-icon" />,
      'process_completed': <CheckCircleOutlined className="history-dot-icon" />,
      'resolved': <CheckCircleOutlined className="history-dot-icon" />,
      'rejected': <CloseCircleOutlined className="history-dot-icon" />,
      'rejected_completed': <CloseCircleOutlined className="history-dot-icon" />,
      'reprocessed': <ClockCircleOutlined className="history-dot-icon" />,
      'completed': <CheckCircleOutlined className="history-dot-icon" />,
    };
    return actionMap[action] || <ClockCircleOutlined className="history-dot-icon" />;
  };

  const getActionColor = (action: string) => {
    const colorMap: { [key: string]: string } = {
      'created': '#52c41a',
      'approved': '#1890ff',
      'process_accepted': '#52c41a',
      'process_completed': '#52c41a',
      'resolved': '#52c41a',
      'rejected': '#ff4d4f',
      'rejected_completed': '#ff4d4f',
      'reprocessed': '#faad14',
      'completed': '#52c41a',
    };
    return colorMap[action] || '#1890ff';
  };

  // Custom panel header with expand/collapse icon
  const panelHeader = () => (
    <div className="card-title-with-icon">
      <div className="card-title">Processing History ({sortedHistories.length})</div>
      <div className="card-expand-icon">
        {isExpanded ? <DownOutlined /> : <RightOutlined />}
      </div>
    </div>
  );

  return (
    <Card 
      className="app-card history-card"
    >
      <div onClick={() => setIsExpanded(!isExpanded)}>
        {panelHeader()}
      </div>
      {isExpanded && (
        sortedHistories.length === 0 ? (
          <div className="empty-history">
            <Text className="empty-history-text">No processing history available</Text>
          </div>
        ) : (
          <Timeline className="history-timeline">
            {sortedHistories.map((history) => (
              <Timeline.Item
                key={history.id}
                dot={getActionIcon(history.action)}
                color={getActionColor(history.action)}
                className="history-item"
              >
                <div className="history-item-content">
                  <div className="history-item-header">
                    <Text strong className="history-action">
                      {history.actionName || history.action}
                    </Text>
                    <Text type="secondary" className="history-time">
                      {moment(history.createdAt).format('DD MMM YYYY, HH:mm:ss')}
                    </Text>
                  </div>
                  
                  <div className="history-item-body">
                    {history.sessionIdx && (
                      <div className="history-session">
                        <Text type="secondary" className="history-session-text">
                          Processing attempt: {history.sessionIdx}
                        </Text>
                      </div>
                    )}
                    
                    {history.metadata?.note && (
                      <div className="history-note">
                        <FileTextOutlined className="history-note-icon" />
                        <Paragraph className="history-note-text">
                          {history.metadata.note}
                        </Paragraph>
                      </div>
                    )}
                    
                    {history.metadata?.fileAttachments && (
                      <FileAttachment 
                        files={history.metadata.fileAttachments}
                        token={token}
                        className="history-attachments"
                      />
                    )}
                  </div>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        )
      )}
    </Card>
  );
};

export default TicketHistory;